import { useEffect, useState } from "react";
import { LogOut, Clock, BadgePercent, FileDown, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { useCart } from "../context/CartContext";
import { B2BAuth } from "../components/store/B2BAuth";
import { B2BOrders } from "../components/store/B2BOrders";
import { B2BPricelist } from "../components/store/B2BPricelist";

const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("b2b_token") || ""}` });

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const PendingBanner = ({ status }) => {
  const rejected = status === "rifiutato";
  return (
    <div data-testid="b2b-pending-banner" className="rounded-2xl border border-[#E7E0D8] bg-white p-10 text-center max-w-xl mx-auto">
      <Clock className="w-12 h-12 mx-auto text-[#7DC242]" />
      <h2 className="font-serif-display font-bold text-2xl mt-5">
        {rejected ? "Richiesta non approvata" : "Account in attesa di approvazione"}
      </h2>
      <p className="mt-3 text-sm text-[#57534E] leading-relaxed">
        {rejected
          ? "La tua richiesta non è stata approvata. Contattaci per maggiori informazioni."
          : "Il nostro team sta verificando i dati della tua azienda. Riceverai l'accesso al listino rivenditore appena la richiesta sarà approvata."}
      </p>
    </div>
  );
};

export default function B2B() {
  const [account, setAccount] = useState(null);
  const [checking, setChecking] = useState(!!sessionStorage.getItem("b2b_token"));
  const [pricelist, setPricelist] = useState(null);
  const [orders, setOrders] = useState([]);
  const { addItem, setOpen, count } = useCart();

  const downloadPdf = async () => {
    try {
      const r = await api.get("/b2b/pricelist/pdf", { headers: authHeaders(), responseType: "blob" });
      downloadBlob(r.data, "listino-rivenditore.pdf");
    } catch (e) {
      logError("Pricelist PDF download failed:", e);
      toast.error("Errore nel download del PDF");
    }
  };

  const downloadOrderPdf = async (id) => {
    try {
      const r = await api.get(`/b2b/orders/${id}/pdf`, { headers: authHeaders(), responseType: "blob" });
      downloadBlob(r.data, `ordine-${id.slice(0, 8)}.pdf`);
    } catch (e) {
      logError("Order PDF download failed:", e);
      toast.error("Errore nel download del PDF ordine");
    }
  };

  const addToCart = (p) => {
    const carton = pricelist?.carton_qty || 1;
    addItem({ slug: p.slug, name: p.name, image: p.image, price: p.price_reseller }, carton);
    toast.success(`${p.name} — cartone da ${carton} pz aggiunto`);
  };

  const reorder = (order) => {
    if (!pricelist) return;
    const priceMap = Object.fromEntries(pricelist.items.map((i) => [i.slug, i]));
    let added = 0;
    order.items.forEach((it) => {
      const cur = priceMap[it.slug];
      if (cur) {
        addItem({ slug: cur.slug, name: cur.name, image: cur.image, price: cur.price_reseller }, it.quantity);
        added += 1;
      }
    });
    toast.success(`${added} prodotti riaggiunti al carrello a prezzo rivenditore`);
    setOpen(true);
  };

  useEffect(() => {
    if (!sessionStorage.getItem("b2b_token")) return;
    api.get("/b2b/me", { headers: authHeaders() })
      .then((r) => setAccount(r.data))
      .catch(() => sessionStorage.removeItem("b2b_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (account?.status === "approvato") {
      api.get("/b2b/pricelist", { headers: authHeaders() }).then((r) => {
        setPricelist(r.data);
        sessionStorage.setItem("b2b_min_order", r.data.min_order);
        sessionStorage.setItem("b2b_carton", r.data.carton_qty);
      }).catch(() => {});
      api.get("/b2b/orders", { headers: authHeaders() }).then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [account]);

  const logout = () => {
    sessionStorage.removeItem("b2b_token");
    sessionStorage.removeItem("b2b_min_order");
    sessionStorage.removeItem("b2b_carton");
    setAccount(null);
    setPricelist(null);
    setOrders([]);
  };

  const renderContent = () => {
    if (account.status !== "approvato") return <PendingBanner status={account.status} />;
    if (!pricelist) return <p className="text-[#78716C]">Caricamento listino...</p>;
    return (
      <>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#2E8A57]/10 text-[#2E8A57] px-5 py-2.5 text-sm font-semibold">
            <BadgePercent className="w-4 h-4" /> Sconto rivenditore: −{pricelist.discount_percent}%
          </div>
          <span data-testid="b2b-min-order-badge" className="rounded-full bg-[#243329] text-[#FDFBF7] px-5 py-2.5 text-sm font-semibold">
            Minimo B2B {pricelist.min_order.toFixed(0)}€ · Cartoni da {pricelist.carton_qty} pz
          </span>
          <button data-testid="b2b-pdf-btn" onClick={downloadPdf} className="inline-flex items-center gap-2 rounded-full border border-[#2E8A57] text-[#2E8A57] px-5 py-2.5 text-sm font-semibold hover:bg-[#2E8A57] hover:text-white transition-colors">
            <FileDown className="w-4 h-4" /> Scarica listino PDF
          </button>
          {count > 0 && (
            <button data-testid="b2b-checkout-btn" onClick={() => { setOpen(true); }} className="btn-tuscan inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold">
              <ShoppingBag className="w-4 h-4" /> Vai al carrello ({count})
            </button>
          )}
        </div>
        <p className="text-xs text-[#78716C] mb-4">Ogni click aggiunge un cartone da {pricelist.carton_qty} pezzi a prezzo rivenditore. Lo sconto viene ricalcolato e garantito al pagamento.</p>

        {orders.length > 0 && <B2BOrders orders={orders} onDownloadPdf={downloadOrderPdf} onReorder={reorder} />}

        <B2BPricelist pricelist={pricelist} onAdd={addToCart} />
      </>
    );
  };

  if (checking) return <div className="py-24 text-center text-[#78716C]">Caricamento...</div>;

  if (!account) return <B2BAuth onAuthed={setAccount} />;

  return (
    <div data-testid="b2b-dashboard" className="max-w-6xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-2">Area Rivenditori B2B</p>
          <h1 className="font-serif-display font-bold text-3xl sm:text-4xl">{account.company}</h1>
          <p className="text-sm text-[#78716C] mt-1">P.IVA {account.vat} · {account.email}</p>
        </div>
        <button data-testid="b2b-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#2E8A57]">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>
      {renderContent()}
    </div>
  );
}
